/**
 * StopButton - Button to stop streaming generation
 *
 * Features:
 * - Visible during streaming
 * - Stops SSE connection
 * - Maintains partial message
 * - variant="compact": icon-only for inline use in input bar
 */

import { Button } from "@/components/base/buttons/button";
import { Stop } from "@untitledui/icons";
import { useTranslation } from "react-i18next";

interface StopButtonProps {
    onStop: () => void;
    isStreaming: boolean;
    /** Icon-only variant for inline use in input/status bar */
    variant?: "default" | "compact";
}

export const StopButton = ({ onStop, isStreaming, variant = "default" }: StopButtonProps) => {
    const { t } = useTranslation(["common"]);

    if (!isStreaming) {
        return null;
    }

    const stopLabel = t("aiChat.actions.stop", { defaultValue: "Stop" });

    if (variant === "compact") {
        return (
            <button
                type="button"
                onClick={onStop}
                aria-label={stopLabel}
                title={stopLabel}
                className="flex items-center justify-center w-8 h-8 rounded-md text-tertiary hover:text-primary hover:bg-secondary border border-transparent hover:border-secondary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
                <Stop className="size-4" />
            </button>
        );
    }

    return (
        <Button onClick={onStop} color="secondary" size="sm" className="flex items-center gap-2">
            <Stop className="size-4" />
            {stopLabel}
        </Button>
    );
};

